import React, { useEffect } from 'react';
import { useThemeStore, useAppStore, useSOWDataStore, useTranslation, useSOWTasksStore } from '../../store';
import '../../css/Description.css';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import NoData from '../NoData';
import { queryParamsService } from '../../services/queryParams';
import { isValueEmpty, decodeHtml } from '../../services/utils';

const DescriptionTab = () => {
  const { isOnline } = useAppStore();
  const { sowData, sowLoading, sowError, fetchSOWData } = useSOWDataStore();
  const {initializeTasks} = useSOWTasksStore();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchData = async () => {
      queryParamsService.initialize();
      try {
        await fetchSOWData();
      } catch (error) {
        // The store records the failure in sowError, which the tab renders
        console.error('Failed to load SOW data:', error);
        return;
      }
      initializeTasks(t);
    };
    fetchData();
  }, []);
  
  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 5) return t('na');
    const [year, month, day, hour, minute] = dateArray;
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };
  
  const extractDescription = (data) => {
    let result = '';
    try {
        const contentJSON = JSON.parse(data);
        if ( typeof contentJSON === 'object' && !isValueEmpty(contentJSON)) {
            // if it is an object, it is a list
            for (let i=0;i< contentJSON.length;i++) {
                if (!isValueEmpty(result)) {
                    result += '<hr>'
                }
                let entry = contentJSON[i];
                result += generateDescriptionEntry(entry);
            }
        }
    } catch (e) {
        result = data
        // hide any errors for now
    }
    return result;
  };
  
  const generateDescriptionEntry = (data) => {
    let result = '';
    if (!isValueEmpty(data)) {
      result = '<div class="description-entry">'
      result += '<div class="task-entry">'
      result += data.description;
      result += '</div>'

      if (data.measures && data.measures.length > 0) {
          result += '<div class="description-measures">'
          result += `<h6>${t('measurements')}</h6>`;
          result += '<ul class="description-measures-list">';
          
          // Add each measure as a list item
          data.measures.forEach(measure => {
              const mandatoryText = measure.mandatory ? ' (Mandatory)' : ' (Optional)';
              result += `<li>${measure.name}${mandatoryText}</li>`;
          });
          
          result += '</ul>';
          result += '</div>'
      }
      result += '</div>'
    }
    return result;
  };
  
  const getTechnicalEmailData = () => {
    const { technicianEmailData } = sowData.reportedIssue;
    
    // Helper function to check if value is truly empty (including &nbsp; and whitespace)
    const isEmptyValue = (value) => {
      if (!value) return true;
      const strValue = value.toString();
      // Remove all &nbsp; and trim whitespace
      const cleanedValue = strValue.replace(/&nbsp;/g, '').trim();
      return cleanedValue === '';
    };

    // Helper function to check if key is non-empty
    const isValidKey = (key) => {
      return key && key.toString().trim() !== '';
    };

    // Filter to get only valid entries
    const validEntries = Object.entries(technicianEmailData).filter(([key, value]) => {
      return isValidKey(key) && !isEmptyValue(value);
    });

    // Return null if no valid entries
    if (validEntries.length === 0) {
      return null;
    }

    return (
      <>
        <h3>{t('technicianEmailData')}</h3>
        <div className="technician-data">
          {validEntries.map(([key, value]) => (
            <div key={key} className="data-row">
              <span className="data-label">{t(key.replace(".", ""))}</span>
              <span 
                className="data-value"
                dangerouslySetInnerHTML={{ __html: decodeHtml(value) }}></span>
            </div>
          ))}
        </div>
      </>
    );
  };
  
  if (sowLoading) {
    return (
      <Loading message={t('loadingWO')}/>
    );
  }

  if (sowError) {
    return (
      <LoadingError message={t('errorLoadingWO', {'error': sowError})}/>
    );
  }
  
  if (!sowData) {
    return (
      <NoData/>
    );
  }   
  
  return (
    <div className="tab-content des">
      {/* Work Order Display */}
        <div className="workorder-container">
          {/* Header Section */}
          <div className="header-section">
            <h1>{t('workOrder')} {sowData.workorderKey}</h1>
            <div className="contact-info">
              <p><strong>{t('hypertecHotline')}</strong> {sowData.hypertecHotline}</p>
              <p><strong>{t('hypertecEmail')}</strong> {sowData.hypertecEmail}</p>
            </div>
          </div>
    
          {/* Customer Information */}
          <div className="section">
            <h2>{t('customerInformation')}</h2>
            <div className="customer-details">
              <p><strong>{t('name')}</strong> {sowData.customer.customerName}</p>
              <p><strong>{t('email')}</strong> {sowData.customer.customerEmail}</p>
              <p><strong>{t('company')}</strong> {sowData.customer.customerCompany}</p>
              <p><strong>{t('address')}</strong> {sowData.customer.customerAddress}, {sowData.customer.customerCity}, {sowData.customer.customerStateProvince}</p>
            </div>
          </div>
    
          {/* Product Information */}
          <div className="section">
            <h2>{t('productInformation')}</h2>
            <div className="product-details">
              <p><strong>{t('deviceType')}</strong> {sowData.productInformation.deviceType}</p>
              <p><strong>{t('model')}</strong> {sowData.productInformation.ciaraModel}</p>
              <p><strong>{t('serialNumber')}</strong> {sowData.productInformation.ciaraSerial}</p>
            </div>
          </div>
    
          {/* Reported Issue */}
          <div className="section">
            <h2>{t('reportedIssue')}</h2>
            <div 
              className="issue-description" 
              dangerouslySetInnerHTML={{ __html: sowData.reportedIssue.description }} 
            />
            
            {getTechnicalEmailData()}
          </div>
    
          {/* Maintenance Window */}
          <div className="section">
            <h2>{t('maintenanceWindow')}</h2>
            <div className="maintenance-details">
              <p><strong>{t('scheduledDate')}</strong> {formatDate(sowData.maintenanceWindow.servicingDate)}</p>
              <p><strong>{t('pointOfContact')}</strong> {sowData.maintenanceWindow.pointOfContact}</p>
            </div>
          </div>
    
          {/* Scope of Work */}
          <div className="section">
            <h2>{t('scopeOfWork')}</h2>
            
            <div className="scope-details">
              <h3>{t('description')}</h3>
              <div 
                className="sow-content" 
                dangerouslySetInnerHTML={{ __html: extractDescription(sowData.scopeOfWork.sow) }} 
              />
              
              <h3>{t('specialInstructions')}</h3>
              <div 
                className="special-instructions" 
                dangerouslySetInnerHTML={{ __html: decodeHtml(sowData.scopeOfWork.specialInstructions) }} 
              />
            </div>
    
            <h3>{t('components')}</h3>
            <div className="components-list">
              {sowData.scopeOfWork.components.map((component, index) => (
                <div key={index} className="component-item">
                  <h4>{component.name}</h4>
                  <div className="component-details">
                    <p><strong>{t('code')}</strong> {component.code}</p>
                    <p><strong>{t('partNumber')}</strong> {component.partNumber}</p>
                    <p><strong>{t('quantity')}</strong> {component.quantity}</p>
                    {component.serial && (<p><strong>{t('serialN')}</strong> {component.serial}</p>)}
                    <p><strong>{t('status')}</strong> {component.status}</p>
                    <p><strong>{t('warehouse')}</strong> {component.warehouseCode} ({component.binCode})</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

export default DescriptionTab;